import React, { useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { QRModal } from '../components/QRModal';
import { useAuthStore } from '../stores/useAuthStore';
import { useRouteId } from '../lib/routeHelper';
import { useServicePalette } from '../hooks/useServicePalette';
import { useCurrencySymbol } from '../lib/currency';
import { DollarSign, Smartphone } from 'lucide-react';
import { apiClient } from '../lib/api';
import { ErrorModal } from '../components/ErrorModal';
import { useErrorModal } from '../hooks/useErrorModal';
import { useCreateBatchOrders, useCreateOrder } from '../hooks/queries/useOrders';
import { useCartStore } from '../stores/useCartStore';
import { PendingCheckoutResponse } from '../types';
import { ExternalPaymentModal } from '../components/ExternalPaymentModal';

interface PaymentMethod {
  id: string;
  name: string;
  icon: React.ReactNode;
  type: 'credit_card' | 'transfer' | 'cash' | 'qr' | 'mercadopago';
}

export function PaymentPage() {
  const { tableId } = useParams<{ tableId: string }>();
  const navigate = useNavigate();
  const { showErrorModal, errorModalMessage, showError, hideError } = useErrorModal();
  const location = useLocation();
  const createOrderMutation = useCreateOrder();
  const createBatchOrdersMutation = useCreateBatchOrders();
  const currencySymbol = useCurrencySymbol();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('cash');
  const [processing, setProcessing] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [showExternalPaymentModal, setShowExternalPaymentModal] = useState(false);
  const [mpPreferenceId, setMpPreferenceId] = useState<string | null>(null);
  const [mpPublicKey, setMpPublicKey] = useState<string | null>(null);
  const [mpLoading, setMpLoading] = useState(false);
  const mpBrickRef = useRef<HTMLDivElement>(null);

  const { paymentType, unpaidOrders, wasScheduled, totalAmount } = location.state || {};
  const [localPendingOrders, setLocalPendingOrders] = useState(location.state?.pendingOrdersToSubmit);
  const [ordersToShow, setOrdersToShow] = useState(unpaidOrders);
  const { isTakeAway, orderTiming } = useAuthStore();
  const { idempotencyKey, regenerateIdempotencyKey } = useCartStore();
  const [pendingCheckout, setPendingCheckout] = useState<PendingCheckoutResponse | null>(null);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const routeId = useRouteId();
  const palette = useServicePalette();


  const paymentMethods: PaymentMethod[] = ([
    {
      id: 'cash',
      name: 'Efectivo / QR',
      icon: <DollarSign className="w-5 h-5" />,
      type: 'cash'
    },
    {
      id: 'mercadopago',
      name: 'Tarjetas de Crédito / Débito',
      icon: <Smartphone className="w-5 h-5" />,
      type: 'mercadopago'
    }
  ] as PaymentMethod[]).filter(method => {
    // If it's a scheduled order ('week'), we don't allow cash
    if ((orderTiming === 'week' || wasScheduled) && method.type === 'cash') return false;
    return true;
  });

  React.useEffect(() => {
    // Set first available payment method as default if cash is not available
    if ((orderTiming === 'week' || wasScheduled) && selectedPaymentMethod === 'cash') {
      setSelectedPaymentMethod('mercadopago');
    }
  }, [orderTiming, wasScheduled]);

  React.useEffect(() => {
    const loadOrders = async () => {
      // Si hay órdenes pendientes de crear, no intentar cargar del backend
      if (localPendingOrders) {
        return;
      }

      if (paymentType === 'individual') {
        const clientOrders = await apiClient.getClientUnpaidOrders();
        setOrdersToShow(clientOrders);
      } else if (paymentType === 'table' && tableId) {
        const tableOrders = await apiClient.getUnpaidOrders(tableId);
        setOrdersToShow(tableOrders);
      }
    };

    // const checkPendingCheckout = async () => {
    //   try {
    //     const response = await apiClient.getPendingCheckout();
    //     if (response && response.abandoned_orders?.length > 0) {
    //       setPendingCheckout(response);
    //       // setShowPendingModal(true);
    //     }
    //   } catch (e) {
    //     console.error('Failed to check pending checkout:', e);
    //   }
    // };

    if (paymentType) {
      loadOrders();
      // Solo chequear pendientes si estamos intentando crear una orden nueva
      if (localPendingOrders) {
        // checkPendingCheckout();
      }
    }
  }, [paymentType, tableId, localPendingOrders]);

  const handleDiscardPending = async () => {
    if (!pendingCheckout?.abandoned_orders?.[0]?.uuid) return;
    try {
      setProcessing(true);
      // Cancelar todas las órdenes abandonadas
      const uuids = pendingCheckout.abandoned_orders.map(o => o.uuid);
      if (uuids.length > 1) {
        await apiClient.cancelOrdersBatch(uuids);
      } else {
        await apiClient.cancelOrder(uuids[0]);
      }
      setShowPendingModal(false);
      setPendingCheckout(null);
      // Al descartar, regeneramos la clave para que el siguiente intento sea "nuevo"
      regenerateIdempotencyKey();
    } catch (e) {
      showError('No se pudo cancelar la orden pendiente.');
    } finally {
      setProcessing(false);
    }
  };

  const handleContinuePending = async () => {
    // La orden ya existe en el backend. Cerramos el modal y cargamos las órdenes
    // pendientes de pago. El usuario puede entonces tocar "Pagar" normalmente.
    setShowPendingModal(false);
    setLocalPendingOrders(null); // No crear orden nueva, la orden ya existe

    try {
      // Cargar las órdenes no pagadas del backend (incluye la pendiente)
      if (paymentType === 'individual') {
        const clientOrders = await apiClient.getClientUnpaidOrders();
        setOrdersToShow(clientOrders);
      } else if (paymentType === 'table' && tableId) {
        const tableOrders = await apiClient.getUnpaidOrders(tableId);
        setOrdersToShow(tableOrders);
      }
    } catch (e) {
      console.error('Error loading pending orders:', e);
      showError('No se pudieron cargar las órdenes pendientes.');
    }
  };
  const handlePayment = async (confirmedExternal: boolean = false) => {
    // Allow take-away payments even if tableId is falsy (visual route id may be used)
    if ((!tableId && !isTakeAway) || (!ordersToShow && !localPendingOrders)) return;

    if (selectedPaymentMethod === 'mercadopago' && !confirmedExternal) {
      setShowExternalPaymentModal(true);
      return;
    }

    setProcessing(true);

    try {
      let currentOrdersToShow = ordersToShow;

      if (localPendingOrders) {
        const orderData = localPendingOrders.map((o: any) => ({
          ...o,
          idempotency_key: idempotencyKey
        }));

        if (orderData.length > 1) {
          await createBatchOrdersMutation.mutateAsync(orderData);
        } else {
          await createOrderMutation.mutateAsync(orderData[0]);
        }

        setLocalPendingOrders(null);

        // Limpiar el estado del historial para que el botón "Atrás" no vuelva a ver órdenes pendientes
        navigate(location.pathname, {
          replace: true,
          state: { ...location.state, pendingOrdersToSubmit: null }
        });

        useAuthStore.getState().setShouldPollOrders(true);

        await new Promise(resolve => setTimeout(resolve, 2000));

        if (paymentType === 'individual') {
          currentOrdersToShow = await apiClient.getClientUnpaidOrders();
        } else if (paymentType === 'table' && tableId) {
          currentOrdersToShow = await apiClient.getUnpaidOrders(tableId);
        }
        setOrdersToShow(currentOrdersToShow);
      }

      let channel = '';
      if (selectedPaymentMethod === 'cash') channel = 'cash';
      else if (selectedPaymentMethod === 'mercadopago') channel = 'mercadopago';

      const paymentData: any = {
        method: selectedPaymentMethod,
        amount: currentOrdersToShow?.total_amount_owed?.toString() || totalAmount?.toString() || '0',
        channel,
        // For take-away, do not send a table UUID (set to null)
        table_uuid: isTakeAway ? null : tableId
      };

      let response;
      if (paymentType === 'table') {
        response = await apiClient.request('/payments/all-table/', {
          method: 'POST',
          body: JSON.stringify(paymentData),
        });
      } else {
        response = await apiClient.createPayment(paymentData);
      }


      if (selectedPaymentMethod === 'mercadopago' && (response as any).payment_url) {
        window.location.href = (response as any).payment_url;
        return;
      }


      if (selectedPaymentMethod === 'qr') {
        setPaymentCompleted(true);
        setShowQRModal(true);
        return;
      }


      setPaymentCompleted(true);
      setTimeout(() => {
        navigate(`/confirmation/${routeId || tableId}`, {
          state: {
            orderNumber: 'PAID-' + Date.now(),
            total: ordersToShow?.total_amount_owed,
            paymentMethod: paymentMethods.find(pm => pm.id === selectedPaymentMethod)?.name
          }
        });
      }, 3000);

    } catch (error: any) {
      console.error('Error creating payment:', error);

      const errorMessage = error.message || error.toString();

      if (errorMessage.includes('Stock insuficiente')) {
        try {
          const jsonStartIndex = errorMessage.indexOf('{');
          if (jsonStartIndex !== -1) {
            const jsonPart = errorMessage.substring(jsonStartIndex);
            const parsed = JSON.parse(jsonPart);
            if (parsed.product) {
              showError(parsed.product);
              return;
            }
          }
        } catch (e) {
          console.error("Failed to parse stock error", e);
        }
        showError('Uno o más productos no tienen stock suficiente.');
        return;
      }

      showError('Parece que estamos experimentando problemas.');
    } finally {
      setProcessing(false);
    }
  };

  const handleBack = () => {
    // Si la orden acaba de ser creada (viene de confirmación), volver al dashboard
    // donde se ve la orden pendiente, en lugar de al carrito vacío.
    if (location.state?.orderCreated) {
      navigate(`/dashboard/${tableId}`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  React.useEffect(() => {
    if (mpPublicKey && mpPreferenceId && mpLoading && mpBrickRef.current) {
      if (!document.getElementById("mp-sdk-script")) {
        const script = document.createElement('script');
        script.src = "https://sdk.mercadopago.com/js/v2";
        script.async = true;
        script.id = "mp-sdk-script";
        script.onload = () => {
          // @ts-ignore
          const mp = new window.MercadoPago(mpPublicKey);
          // @ts-ignore
          const bricksBuilder = mp.bricks();
          // @ts-ignore
          bricksBuilder.create("wallet", "walletBrick_container", {
            initialization: {
              preferenceId: mpPreferenceId
            }
          });
        };
        document.body.appendChild(script);
      } else {
        // @ts-ignore
        const mp = new window.MercadoPago(mpPublicKey);
        // @ts-ignore
        const bricksBuilder = mp.bricks();
        // @ts-ignore
        bricksBuilder.create("wallet", "walletBrick_container", {
          initialization: {
            preferenceId: mpPreferenceId
          }
        });
      }
    }
    return () => {
      const container = document.getElementById("walletBrick_container");
      if (container) container.innerHTML = '';
    };
  }, [mpPublicKey, mpPreferenceId, mpLoading]);

  const handleQRModalClose = () => {
    setShowQRModal(false);
    setTimeout(() => {
      navigate(`/confirmation/${tableId}`, {
        state: {
          orderNumber: 'PAID-' + Date.now(),
          total: ordersToShow?.total_amount_owed,
          paymentMethod: paymentMethods.find(pm => pm.id === selectedPaymentMethod)?.name
        }
      });
    }, 3000);
  };

  // Rating modal removed -- navigation handled after payment/QR close

  const getTotalPrice = () => {
    if (!ordersToShow) return 0;
    return ordersToShow.total_amount_owed;
  };

  // Si hay órdenes pendientes de crear, mostrar la página aunque no haya ordersToShow
  const shouldShowContent = ordersToShow || localPendingOrders;

  if (!shouldShowContent) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header title="Pago" showBack onBack={handleBack} />
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400">Cargando información de pago...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-32">
        <Header
          title="Pago"
          showBack
          onBack={handleBack}
        />

        <div className="p-4 space-y-6">
          {!isTakeAway && (
            <div className={`${palette.bgLight} ${palette.bgDark} rounded-lg p-4 border ${palette.borderAccent} dark:border-orange-800`}>
              <h3 className={`font-medium ${palette.textAccent} dark:text-orange-100 mb-1`}>
                Tipo de pago:
              </h3>
              <p className={`${palette.textAccent} dark:text-orange-300`}>
                {paymentType === 'individual' ? 'Pagando solo mis órdenes' : 'Pagando toda la mesa'}
              </p>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              {paymentType === 'individual' ? 'Pago Individual' : 'Pago de Mesa'}
            </h3>

            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                {isTakeAway ? 'Pedido para retirar - Órdenes pendientes' : (ordersToShow ? `Mesa ${ordersToShow.table_number} - Órdenes pendientes` : 'Pedido pendiente')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                Total a pagar: {currencySymbol}{(ordersToShow?.total_amount_owed || totalAmount || 0).toFixed(2)}
              </p>
            </div>

            {ordersToShow && ordersToShow.orders && (
              <div className="space-y-3 mt-4">
                <h4 className="font-medium text-gray-900 dark:text-white">Detalle de órdenes:</h4>
                {ordersToShow.orders.flatMap((order: any) =>
                  (order.order_products as any[]).map((item, index) => (
                    <div key={`${order.uuid || (order as any).id}-${index}`} className="flex justify-between items-center">
                      <span className="text-gray-700 dark:text-gray-300">
                        {item.quantity}x {item.product_details?.name || item.offer_details?.name || 'Producto'}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {currencySymbol}{((item.product_details?.price || item.offer_details?.price || 0) * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          {/* <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowTipSection(!showTipSection)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Heart className="w-5 h-5 text-primary" />
              <span className="font-semibold text-gray-900 dark:text-white">
                Dejar Propina
              </span>
            </div>
            {showTipSection ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {showTipSection && (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Selecciona un mozo para dejar propina:
              </p>

              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {waiters.map((waiter) => (
                  <div key={waiter.id}>
                    <label
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedWaiter === waiter.id
                          ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="waiter"
                          value={waiter.id}
                          checked={selectedWaiter === waiter.id}
                          onChange={(e) => { setSelectedWaiter(e.target.value); setTipPercentage(0); setCustomTipAmount(''); }}
                          className="sr-only"
                        />
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            selectedWaiter === waiter.id
                              ? 'border-sky-500 bg-sky-500'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          {selectedWaiter === waiter.id && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {waiter.name}
                        </span>
                      </div>
                    </label>

                    {selectedWaiter === waiter.id && (
                      <div className="mt-2 ml-7 space-x-2 flex items-center flex-wrap">
                        {[5, 10, 20].map((pct) => (
                          <button
                            key={pct}
                            onClick={() => {
                              setCustomTipAmount('');
                              setTipPercentage(pct);
                            }}
                            className={`px-3 py-1 rounded-full text-sm font-medium border ${
                              tipPercentage === pct
                                ? 'bg-sky-500 text-white border-sky-500'
                                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            {pct}%
                          </button>
                        ))}
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600 dark:text-gray-400">$</span>
                          <input
                            type="number"
                            value={customTipAmount || ''}
                            onChange={(e) => {
                              setTipPercentage(0);
                              setCustomTipAmount(e.target.value);
                            }}
                            placeholder="Otro"
                            className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            step="0.01"
                            min="0"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {(selectedWaiter && (tipPercentage || customTipAmount)) && (
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-green-800 dark:text-green-200 font-medium">
                      Propina seleccionada:
                    </span>
                    <span className="text-green-900 dark:text-green-100 font-bold">
                      {currencySymbol}{calculateTipAmount().toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div> */}

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              Método de Pago
            </h3>
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <label
                  key={method.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${selectedPaymentMethod === method.id
                    ? `border-orange-500 ${palette.bgLight} ${palette.bgDark}`
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value={method.id}
                    checked={selectedPaymentMethod === method.id}
                    onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === method.id
                    ? 'border-orange-500 bg-orange-500'
                    : 'border-gray-300 dark:border-gray-600'
                    }`}>
                    {selectedPaymentMethod === method.id && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <div className="text-gray-700 dark:text-gray-300">
                      {method.icon}
                    </div>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {method.name}
                    </span>
                  </div>
                  {method.type === 'credit_card' && (
                    <div className="flex gap-2">
                      <div className="w-8 h-5 bg-sky-500 rounded text-white text-xs flex items-center justify-center font-bold">
                        MC
                      </div>
                      <div className="w-8 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
                        V
                      </div>
                    </div>
                  )}
                  {method.type === 'qr' && (
                    <div className="w-8 h-5 bg-gray-800 rounded text-white text-xs flex items-center justify-center">
                      QR
                    </div>
                  )}
                  {method.type === 'mercadopago'}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4">
          {selectedPaymentMethod === 'mercadopago' && mpPreferenceId && mpPublicKey ? (
            <div>
              <div ref={mpBrickRef} id="walletBrick_container" className="mb-4" />
              <button
                className="w-full bg-gray-300 text-gray-500 py-3 rounded-lg font-medium mb-2" disabled
              >
                Finaliza el pago en MercadoPago
              </button>
              <button
                onClick={() => {
                  setMpPreferenceId(null);
                  setMpPublicKey(null);
                  setMpLoading(false);
                }}
                className={`w-full ${palette.bgLight} ${palette.textAccent} py-2 rounded-lg mt-1`}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              onClick={() => handlePayment(false)}
              disabled={processing || paymentCompleted}
              className={`w-full bg-gradient-to-r ${palette.primaryFrom} ${palette.primaryTo} hover:${palette.primaryFromHover} hover:${palette.primaryToHover} disabled:bg-gray-400 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center`}
            >
              {processing || paymentCompleted ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {processing ? 'Procesando Pago...' : 'Pago Completado'}
                </div>
              ) : (
                `Pagar`
              )}
            </button>
          )}
        </div>
      </div>
      <ErrorModal
        isOpen={showErrorModal}
        onClose={hideError}
        title="Error de Pago"
        message={errorModalMessage}
      />
      <QRModal
        isOpen={showQRModal}
        onClose={handleQRModalClose}
        amount={ordersToShow?.total_amount_owed || 0}
      />

      <ExternalPaymentModal
        isOpen={showExternalPaymentModal}
        onClose={() => setShowExternalPaymentModal(false)}
        onConfirm={() => {
          setShowExternalPaymentModal(false);
          handlePayment(true);
        }}
        isProcessing={processing}
      />

      {/* Modal de Pago Pendiente */}
      {showPendingModal && pendingCheckout && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className={`p-8 bg-gradient-to-br ${palette.primaryFrom} ${palette.primaryTo} text-white relative overflow-hidden`}>
              <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
              <div className="relative">
                <h3 className="text-2xl font-bold mb-2">¡Pago Pendiente!</h3>
                <p className="text-white/80">Tenés un pedido esperando confirmación de pago.</p>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400 font-medium">Total:</span>
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  {currencySymbol}{pendingCheckout.total_amount.toFixed(2)}
                </span>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleContinuePending}
                  disabled={processing}
                  className={`w-full bg-gradient-to-r ${palette.primaryFrom} ${palette.primaryTo} hover:${palette.primaryFromHover} hover:${palette.primaryToHover} text-white py-4 rounded-2xl font-bold transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2`}
                >
                  Continuar Pago
                </button>

                <button
                  onClick={handleDiscardPending}
                  disabled={processing}
                  className="w-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 py-3 rounded-xl font-medium transition-all"
                >
                  Descartar y empezar nuevo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
